// src/user/UserVideoList.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Switch,
  TextField,
  Box,
  Button,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import ProfessionalTable from '../components/ProfessionalTable';
import DashboardPageHeader from '../components/DashboardPageHeader';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const ThumbnailPreview = ({ src, title }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <Box
        sx={{
          width: 120,
          height: 68,
          borderRadius: 1,
          bgcolor: 'grey.100',
          border: '1px dashed',
          borderColor: 'grey.300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          color: 'text.secondary',
        }}
      >
        {src && failed ? 'Failed to load' : 'No thumbnail'}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={title || 'Video thumbnail'}
      sx={{
        width: 120,
        height: 68,
        borderRadius: 1,
        objectFit: 'cover',
        border: '1px solid',
        borderColor: 'grey.200',
      }}
      onError={() => setFailed(true)}
    />
  );
};

function UserVideoList() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingImpressions, setLoadingImpressions] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [openPlayer, setOpenPlayer] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [videoImpressions, setVideoImpressions] = useState({}); // Map of VideoId -> ImpressionCount
  const [totalImpressions, setTotalImpressions] = useState(0);

  // Test if a URL is accessible
  const testUrlAccess = useCallback(async (url, type) => {
    try {
      console.log(`🔍 Testing ${type} URL:`, url);
      const response = await fetch(url, { method: 'HEAD' });
      console.log(`🔗 ${type} URL test result:`, {
        url,
        status: response.status,
        statusText: response.statusText,
        accessible: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      return response.ok;
    } catch (error) {
      console.error(`❌ ${type} URL test failed:`, { 
        url, 
        error: error.message,
        errorType: error.name,
        stack: error.stack
      });
      return false;
    }
  }, []);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API.userVideos);
      console.log('📹 User Videos API Response:', res.data);
      console.log('📹 User Videos data:', res.data?.videos);
      
      // Debug individual video URLs
      if (res.data?.videos) {
        res.data.videos.forEach(async (video, index) => {
          console.log(`🎬 User Video ${index + 1}:`, {
            VideoID: video.VideoID,
            Title: video.Title,
            VideoUrl: video.VideoUrl,
            ThumbnailUrl: video.ThumbnailUrl,
            CategoryName: video.CategoryName,
            Status: video.Status,
            IsActive: video.IsActive
          });
          
          // Test URL accessibility
          if (video.ThumbnailUrl) {
            await testUrlAccess(video.ThumbnailUrl, 'Thumbnail');
          }
          if (video.VideoUrl) {
            await testUrlAccess(video.VideoUrl, 'Video');
          }
        });
      }
      
      setVideos(res.data?.videos || []);
    } catch (err) {
      console.error('❌ Failed to fetch user videos:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to fetch your videos');
    } finally {
      setLoading(false);
    }
  }, [testUrlAccess]);

  const fetchVideoImpressions = useCallback(async () => {
    setLoadingImpressions(true);
    try {
      const res = await api.get(API.userVideoImpressions);
      console.log('📊 Video Impressions API Response:', res.data);
      console.log('📊 Video Impressions Raw Data:', JSON.stringify(res.data, null, 2));
      
      if (res.data?.success) {
        // Create a map of VideoId -> ImpressionCount
        // Store keys as numbers to match VideoID from userVideos API
        const impressionsMap = {};
        if (res.data.videoImpressions) {
          res.data.videoImpressions.forEach((item) => {
            // SQL Server returns VideoId (lowercase 'd'), convert to number for consistent lookup
            const videoId = item.VideoId || item.VideoID || item.videoId || item.videoID;
            const count = item.ImpressionCount || item.impressionCount || 0;
            if (videoId) {
              // Store as number to match VideoID from videos list
              const videoIdNum = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;
              impressionsMap[videoIdNum] = count;
              // Also store as string key for fallback
              impressionsMap[videoId] = count;
              console.log(`📊 Mapped VideoId ${videoId} (${videoIdNum}) -> ${count} impressions`);
            }
          });
        }
        console.log('📊 Final Impressions Map:', impressionsMap);
        console.log('📊 Map keys (first 5):', Object.keys(impressionsMap).slice(0, 5));
        setVideoImpressions(impressionsMap);
        setTotalImpressions(res.data.totalImpressions || 0);
      }
    } catch (err) {
      console.error('❌ Failed to fetch video impressions:', err);
      console.error('❌ Error details:', err.response?.data);
      // Don't show error message for impressions - it's not critical
    } finally {
      setLoadingImpressions(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
    fetchVideoImpressions();
  }, [fetchVideos, fetchVideoImpressions]);

  const handlePlay = useCallback((video) => {
    console.log('🎬 Playing video:', video);
    setCurrentVideo(video);
    setOpenPlayer(true);
  }, []);

  const handlePlayerClose = () => {
    setOpenPlayer(false);
    setCurrentVideo(null);
  };


  const handleToggleActive = useCallback(async (videoId, isActive) => {
    try {
      console.log(`🔄 ${isActive ? 'Activating' : 'Deactivating'} video ${videoId}...`);
      
      const res = await api.patch(API.toggleVideoActive(videoId), { isActive });
      
      if (res.data?.success) {
        // Update the local state
        setVideos(prevVideos => 
          prevVideos.map(video => 
            video.VideoID === videoId 
              ? { ...video, IsActive: isActive }
              : video
          )
        );
        
        setSuccessMsg(`Video ${isActive ? 'activated' : 'deactivated'} successfully`);
        console.log(`✅ Video ${videoId} ${isActive ? 'activated' : 'deactivated'}`);
      } else {
        setErrorMsg(`Failed to ${isActive ? 'activate' : 'deactivate'} video`);
      }
    } catch (err) {
      console.error(`❌ Error ${isActive ? 'activating' : 'deactivating'} video:`, err);
      setErrorMsg(err.response?.data?.error || `Failed to ${isActive ? 'activate' : 'deactivate'} video`);
    }
  }, []);

  const handleDeleteClick = useCallback((video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;

    try {
      setDeleting(true);
      const res = await api.delete(API.deleteVideo(videoToDelete.VideoID));
      
      if (res.data?.success) {
        setVideos(prevVideos => prevVideos.filter(v => v.VideoID !== videoToDelete.VideoID));
        setSuccessMsg('Video deleted successfully');
        setDeleteDialogOpen(false);
        setVideoToDelete(null);
      } else {
        setErrorMsg('Failed to delete video');
      }
    } catch (err) {
      console.error('❌ Error deleting video:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to delete video');
    } finally {
      setDeleting(false);
    }
  };

  // Filtered and sorted list based on search term and impressions
  const filteredVideos = useMemo(() => {
    const term = filterTerm.trim().toLowerCase();
    let filtered = videos;

    // Apply search filter if term exists
    if (term) {
      filtered = videos.filter((v) => {
      const title = v.Title?.toLowerCase() ?? '';
      const category = v.CategoryName?.toLowerCase() ?? '';
      const status = v.Status?.toLowerCase() ?? '';
      return (
        title.includes(term) ||
        category.includes(term) ||
        status.includes(term)
      );
    });
    }

    // Sort by impressions (descending) - highest impressions first
    return filtered.sort((a, b) => {
      const videoIdA = a.VideoID;
      const videoIdB = b.VideoID;
      const videoIdANum = typeof videoIdA === 'string' ? parseInt(videoIdA, 10) : videoIdA;
      const videoIdBNum = typeof videoIdB === 'string' ? parseInt(videoIdB, 10) : videoIdB;
      
      const impressionsA = videoImpressions[videoIdA] || 
                          videoImpressions[videoIdANum] || 
                          videoImpressions[String(videoIdA)] || 
                          0;
      const impressionsB = videoImpressions[videoIdB] || 
                          videoImpressions[videoIdBNum] || 
                          videoImpressions[String(videoIdB)] || 
                          0;
      
      // Sort descending (highest impressions first)
      return impressionsB - impressionsA;
    });
  }, [videos, filterTerm, videoImpressions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const tableData = useMemo(
    () =>
      filteredVideos.map((video) => ({
        ...video,
        id: video.VideoID,
      })),
    [filteredVideos]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Thumbnail',
        accessor: 'ThumbnailUrl',
        style: { minWidth: 150 },
        render: (_, row) => (
          <ThumbnailPreview src={row.ThumbnailUrl} title={row.Title} />
        ),
      },
      {
        header: 'Title',
        accessor: 'Title',
        render: (value) => (
          <Typography fontWeight={600}>{value || '—'}</Typography>
        ),
      },
      {
        header: 'Category',
        accessor: 'CategoryName',
        render: (value) => <Typography>{value || '—'}</Typography>,
      },
      {
        header: 'Status',
        accessor: 'Status',
        render: (value) => (
          <Chip
            label={value || 'Pending'}
            color={getStatusColor(value)}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        header: 'Upload Date',
        accessor: 'UploadDate',
        render: (value) => (
          <Typography>
            {value ? new Date(value).toLocaleDateString() : '—'}
          </Typography>
        ),
      },
      {
        header: 'Impressions (Overall)',
        accessor: 'VideoID',
        style: { textAlign: 'center', minWidth: 150 },
        render: (value, row) => {
          // value is VideoID from the video row (likely a number)
          // Try multiple lookup strategies to handle type mismatches
          const videoIdNum = typeof value === 'string' ? parseInt(value, 10) : value;
          const videoIdStr = String(value);
          
          // Try: number key, string key, row.VideoId (if different case)
          const count = videoImpressions[value] || 
                       videoImpressions[videoIdNum] || 
                       videoImpressions[videoIdStr] ||
                       videoImpressions[row.VideoId] || 
                       videoImpressions[row.videoId] ||
                       0;
          
          // Debug only for first video or if count is 0 but map has data
          if ((value === filteredVideos[0]?.VideoID || (count === 0 && Object.keys(videoImpressions).length > 0)) && !loadingImpressions) {
            console.log(`🔍 Looking up impressions for VideoID ${value} (type: ${typeof value}):`, {
              videoId: value,
              videoIdNum: videoIdNum,
              videoIdStr: videoIdStr,
              found: videoImpressions[value],
              foundNum: videoImpressions[videoIdNum],
              foundStr: videoImpressions[videoIdStr],
              impressionsMapKeys: Object.keys(videoImpressions).slice(0, 10),
              count: count
            });
          }
          
          return (
            <Typography fontWeight={count > 0 ? 600 : 400} color={count > 0 ? 'primary.main' : 'text.secondary'}>
              {loadingImpressions ? '...' : count.toLocaleString()}
            </Typography>
          );
        },
      },
      {
        header: 'Preview',
        accessor: 'VideoUrl',
        style: { textAlign: 'center', minWidth: 100 },
        render: (value, row) =>
          value ? (
            <IconButton
              onClick={() => handlePlay(row)}
              size="small"
              color="primary"
              title="Play"
            >
              <PlayCircleIcon />
            </IconButton>
          ) : (
            '—'
          ),
      },
      {
        header: 'Active',
        accessor: 'IsActive',
        style: { textAlign: 'center', minWidth: 110 },
        render: (value, row) => (
          <Switch
            checked={!!value}
            color="primary"
            onChange={(e) =>
              handleToggleActive(row.VideoID, e.target.checked)
            }
          />
        ),
      },
      {
        header: 'Actions',
        accessor: 'VideoID',
        style: { textAlign: 'center', minWidth: 150 },
        render: (_, row) => (
          <Box display="flex" gap={1} justifyContent="center">
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigate(`/videos/${row.VideoID}/edit`)}
              title="Edit"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteClick(row)}
              title="Delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handlePlay, handleToggleActive, handleDeleteClick, navigate, videoImpressions, loadingImpressions]
  );

  return (
    <div>
      <DashboardPageHeader
        icon={VideoLibraryIcon}
        title="My videos"
        subtitle={
          totalImpressions > 0
            ? `Manage uploads and visibility. This month’s impressions: ${totalImpressions.toLocaleString()}.`
            : 'Manage uploads, search your library, and track performance.'
        }
      />

      {/* Search and actions */}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        mb={2}
      >
        <TextField
          placeholder="Search by title, category, status..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setFilterTerm(searchInput.trim());
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={() => setFilterTerm(searchInput.trim())}
          >
            Search
          </Button>
        </Stack>
      </Stack>

      <ProfessionalTable
        columns={columns}
        data={tableData}
        loading={loading}
        emptyMessage={videos.length === 0 ? 'No videos uploaded yet' : 'No videos found matching your search'}
      />

      {/* Video preview dialog */}
      <Dialog open={openPlayer} onClose={handlePlayerClose} maxWidth="md" fullWidth>
        <DialogContent>
          {currentVideo?.VideoUrl ? (
            <video
              key={currentVideo.VideoID}
              width="100%"
              height="auto"
              controls
              src={currentVideo.VideoUrl}
              style={{ borderRadius: 8 }}
              onError={(e) => {
                console.error('❌ Video load error:', currentVideo.VideoUrl);
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'block';
              }}
              onLoad={() => console.log('✅ Video loaded:', currentVideo.VideoUrl)}
            />
          ) : null}
          <div 
            style={{ 
              display: currentVideo?.VideoUrl ? 'none' : 'block',
              textAlign: 'center',
              padding: '2rem',
              backgroundColor: '#f5f5f5',
              borderRadius: 8
            }}
          >
            <Typography variant="h6" color="textSecondary">
              {currentVideo?.VideoUrl ? 'Failed to load video' : 'No video URL available'}
            </Typography>
            {currentVideo?.VideoUrl && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                URL: {currentVideo.VideoUrl}
              </Typography>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{videoToDelete?.Title}"?
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={4000}
        onClose={() => {
          setErrorMsg('');
          setSuccessMsg('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={errorMsg ? 'error' : 'success'}
          onClose={() => {
            setErrorMsg('');
            setSuccessMsg('');
          }}
          sx={{ width: '100%' }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default UserVideoList;
